/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import transformEngineMainUrl from "url:../assets/images/transform_engine_main.png";
import transformEngineInputsUrl from "url:../assets/images/transform_engine_inputs.png";
import transformEngineTransformsUrl from "url:../assets/images/transform_engine_transforms.png";

export const transformEngineContent = `
## Summary

The Transform Engine is the visual workspace at the heart of Dexter. It provides a node-based, drag-and-drop interface for designing and executing data transformation pipelines. 

In the Transform Engine, you work with three primary components:
- **Input Nodes** - Your source data files
- **Transform Nodes** - The transformation logic that converts data between formats
- **Output Nodes** - The resulting transformed data

The Transform Engine allows you to visually connect these components to create transformation workflows. You can save your work as reusable mappings, load previously created mappings, and execute transformations to generate output files.

The interface consists of a toolbar for project management, a control bar for adding nodes, side panels for browsing available inputs and transforms, and a mapping canvas where you design your transformation pipeline.

---

## Interface Overview

<img src="${transformEngineMainUrl}" alt="Transform Engine Main" style="max-width: 1000px; height: auto; display: block; margin: 12px auto;" />

### Key Areas

**1. Navigation Bar**
The navigation bar is used to navigate to different parts of the Dexter application.  The **Transform Engine** is considered the home page.

**2. Toolbar**
The toolbar contains project-level actions and mapping management tools.

**3. Control Bar**
Quick access to add inputs, transformation nodes, and output nodes to your mapping canvas.

**4. Mapping Canvas**
The main workspace where you visually design your transformation pipeline using nodes and connections.

---

## Toolbar Components

The toolbar provides essential project management and mapping operations:

### Project Management

**Clear Transform Engine** - Removes all nodes and connections from the mapping canvas, giving you a fresh start.

**Project Title** - Click to rename your current mapping. The title appears in the center of the toolbar and can be edited inline.

### Mapping Operations

**Save Mapping** - Saves your current transformation pipeline to the server. All nodes, connections, and configurations are preserved.

**Open Mapping** - Opens a modal to browse and load previously saved mappings. You can search through your saved work and restore any mapping.

**Export Mapping (JSON)** - Exports your current mapping as a JSON file to your computer. Useful for backup, version control, or sharing with team members.

**Import Mapping** - Imports a mapping from a JSON file. You can load mappings exported from another Dexter instance or restore from a backup.

### Utilities

**Load Demo** - Loads a pre-configured demonstration mapping that showcases Dexter's capabilities. Useful for learning and testing.

**Open Initialization** - Opens a modal that helps you quickly set up a new mapping by selecting input files and transform types.

**Mapping Context** - A dropdown menu for switching the mapping context, i.e., UC2 (not currently implemented).

---

## Control Bar

The left-side control bar provides quick access to add nodes to your canvas.  The following screenshots show the result of expanding the side panels for the input nodes and transformation nodes respectively.

### Add Input Node
Click the input icon to open the inputs side panel. From here, you can drag files onto the canvas to create input nodes.

<img src="${transformEngineInputsUrl}" alt="Transform Engine Inputs" style="max-width: 1000px; height: auto; display: block; margin: 12px auto;" />

Capabilities:
- Browse all uploaded files
- Filter by file name
- Drag files directly onto the canvas to create input nodes
- View input collections

### Add Transform Node  
Click the transform icon to open the transforms side panel. Browse available transform types and drag them onto the canvas.

<img src="${transformEngineTransformsUrl}" alt="Transform Engine Transforms" style="max-width: 1000px; height: auto; display: block; margin: 12px auto;" />

Capabilities:
- Browse available transform types
- Drag transforms onto the canvas to add them to your pipeline
- View transform descriptions and requirements

### Add Output Node
Click the output icon to automatically add an output node to the canvas. The node is intelligently positioned based on existing transforms.

### Download All Outputs (ZIP)
When enabled, this button allows you to download all completed transformations as a single ZIP archive. Only appears active when at least one output is in a download-ready state.

---

## Working with Mappings

### Creating a New Mapping

1. Start with a clear canvas (use Clear button if needed)
2. Add input nodes by dragging files from the inputs panel
3. Add transform nodes from the transforms panel
4. Create connections between inputs and transforms
5. Add output nodes to capture results
6. Download transform results from output nodes
7. Save your mapping with a descriptive name if needed

### Saving Your Work

Click the Save button in the toolbar. Your mapping is stored on the server with:
- All node positions and configurations
- All connections between nodes
- Transform settings and rules
- Project name and metadata

Saved mappings can be loaded later or exported as JSON files.

### Loading Existing Mappings

Click the Load button to open the Load Mapping modal:
- Browse your saved mappings
- View creation dates
- Select a mapping to load it onto the canvas

**Note**: Loading a mapping replaces your current work. Save any changes first.

### Importing/Exporting Mappings

**Export**: Use "Export Mapping (JSON)" to save a mapping file to your computer. This creates a portable version of your mapping that can be:
- Backed up for safekeeping
- Shared with team members
- Moved between Dexter instances
- Version controlled in Git

**Import**: Use "Import Mapping" to load a JSON file. The mapping is validated and loaded onto the canvas, restoring all nodes and connections.

---

## Best Practices

### Organizing Your Mapping Canvas

- **Group Related Nodes**: Keep nodes that work together close to each other
- **Left-to-Right Flow**: Position inputs on the left, transforms in the middle, outputs on the right
- **Name Your Mappings**: Use descriptive names that explain what the mapping does

### Working Efficiently

- **Save Frequently**: Click Save after major changes to preserve your work
- **Use Collections**: Group related input files into collections for easier management
- **Test Incrementally**: Build your pipeline step-by-step and test each connection
- **Export Backups**: Periodically export important mappings as JSON files

### Troubleshooting

If something isn't working:
1. Check all connections are valid and complete
2. Verify input files are correctly loaded
3. Review any error notifications
4. Try disconnecting and reconnecting nodes
5. Save your work and reload the page if needed


`;
